import {Inject, Injectable} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {select, Store} from '@ngrx/store';
import {ActiveCartFacade} from '@spartacus/cart/base/root';
import {CheckoutQueryFacade} from '@spartacus/checkout/base/root';
import {UserIdService} from '@spartacus/core';
import {CommonConfigurator, CommonConfiguratorUtilsService} from '@spartacus/product-configurator/common';
import {
  Configurator,
  ConfiguratorActions,
  ConfiguratorCartService,
  ConfiguratorSelectors,
  ConfiguratorUtilsService,
} from '@spartacus/product-configurator/rulebased';
import {StateWithConfigurator} from '@spartacus/product-configurator/rulebased/core/state/configurator-state';
import {combineLatest} from 'rxjs';
import {first} from 'rxjs/operators';
import token from '../token/tokens';
import {ProductService} from './product.service';
import {CartService} from './cart.service';
import {ProductUtilitiesService} from './product-utilities.service';
import {selectCancellationUrl} from '../store';
const {PRODUCT_SEARCH_FIELDS_CONFIG} = token.values;
import * as ConfigUtils from '../utils';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DCPConfiguratorCartService extends ConfiguratorCartService {
  constructor(
    protected override store: Store<StateWithConfigurator>,
    protected activeCartFacade: ActiveCartFacade,
    protected override commonConfigUtilsService: CommonConfiguratorUtilsService,
    protected override userIdService: UserIdService,
    protected override configuratorUtilsService: ConfiguratorUtilsService,
    private productService: ProductService,
    private cartService: CartService,
    checkoutQueryFacade: CheckoutQueryFacade,
    private dcpProductUtilitiesService: ProductUtilitiesService,
    @Inject(PRODUCT_SEARCH_FIELDS_CONFIG) protected productSearchFields: string,
  ) {
    super(
      store,
      activeCartFacade,
      commonConfigUtilsService,
      checkoutQueryFacade,
      userIdService,
      configuratorUtilsService,
    );
  }

  /**
   * Adds a configuration to the cart, specified by the product code, a configuration ID and configuration owner key.
   *
   * @param productCode - Product code
   * @param configId - Configuration ID
   * @param owner Configuration owner
   */
  override addToCart(productCode: string, configId: string, owner: CommonConfigurator.Owner): void {
    combineLatest([
      this.activeCartFacade.requireLoadedCart(),
      this.productService.searchForProduct(productCode, this.productSearchFields),
      this.store.select(selectCancellationUrl),
    ])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([cartState, product, cancellationUrl]) => {
        this.store
          .pipe(select(ConfiguratorSelectors.getConfigurationFactory(owner.key)))
          .pipe(first(), untilDestroyed(this))
          .subscribe((configuration) => {
            const licenseMaterial = ConfigUtils.getSelectedGroupAttributeValue(
              configuration,
              ConfigUtils.TECHNICAL_GROUP_NAME,
              ConfigUtils.LICENSE_MATERIAL_ATTRIBUTE,
            );
            const transactionType = ConfigUtils.getSelectedGroupAttributeValue(
              configuration,
              ConfigUtils.TECHNICAL_GROUP_NAME,
              ConfigUtils.TRANSACTION_TYPE_ATTRIBUTE,
            );
            const technicalName = this.dcpProductUtilitiesService.getProductTechnicalName(
              product,
              licenseMaterial?.toString() || '',
            );
            const addToCartParameters: Configurator.AddToCartParameters = {
              userId: 'current',
              cartId: this.commonConfigUtilsService.getCartId(cartState),
              productCode,
              quantity: 1,
              configId,
              owner,
              transactionType,
              technicalName,
              cancellationUrl,
            };
            this.store.dispatch(new ConfiguratorActions.AddToCart(addToCartParameters));
          });
      });
  }

  /**
   * Updates a cart entry, specified by the configuration.
   * The cart entry number for the entry that owns the configuration can be told from the configuration's owner ID
   *
   * Overriding this spartacus function to pass transaction type as an additional request parameter
   *
   * @param configuration - Configuration
   */
  override updateCartEntry(configuration: Configurator.Configuration): void {
    combineLatest([
      this.activeCartFacade.requireLoadedCart(),
      this.userIdService.getUserId(),
      this.productService.searchForProduct(configuration.productCode, this.productSearchFields),
    ])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([cartState, userId, product]) => {
        const licenseMaterial = ConfigUtils.getSelectedGroupAttributeValue(
          configuration,
          ConfigUtils.TECHNICAL_GROUP_NAME,
          ConfigUtils.LICENSE_MATERIAL_ATTRIBUTE,
        );
        const transactionType = ConfigUtils.getSelectedGroupAttributeValue(
          configuration,
          ConfigUtils.TECHNICAL_GROUP_NAME,
          ConfigUtils.TRANSACTION_TYPE_ATTRIBUTE,
        );
        const technicalName = this.dcpProductUtilitiesService.getProductTechnicalName(
          product,
          licenseMaterial?.toString() || '',
        );
        const parameters: Configurator.UpdateConfigurationForCartEntryParameters = {
          userId,
          cartId: this.commonConfigUtilsService.getCartId(cartState),
          cartEntryNumber: configuration.owner.id,
          configuration,
          transactionType,
          technicalName,
        };
        this.store.dispatch(new ConfiguratorActions.UpdateCartEntry(parameters));
      });
  }
}
